import * as React from "react";
import { useLocation } from "wouter";
import { MapPin, Plus, Home as HomeIcon, Trash2, Pencil, Package, MoreHorizontal } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext.js";
import { spaceHasIntent } from "@homethyst/shared/schemas";
import { cn } from "@/lib/utils.js";
import { apiFetch } from "@/lib/api.js";
import { Modal } from "@/components/Modal.js";
import { SearchableSelect } from "@/components/SearchableSelect.js";
import { BulkSpacesWizard } from "@/components/BulkSpacesWizard.js";
import { RoomSetupFlow } from "@/components/RoomSetupFlow.js";
import { ConfirmDialog } from "@/components/ConfirmDialog.js";
import { useToast } from "@/components/Toast.js";
import { AdSlot } from "@/components/AdSlot.js";

interface Space {
  id: string; name: string; type: string; roomType: string | null; parentId: string | null; icon: string | null; description: string | null;
  purpose: string | null; activities: string[] | null; currentState: string | null; desiredState: string | null;
}

const ROOM_ICONS: Record<string, string> = {
  kitchen: "🍳", "living room": "🛋️", "dining room": "🍽️", bedroom: "🛏️", bathroom: "🚿",
  garage: "🚗", attic: "📦", basement: "🏚️", office: "💻", "laundry room": "🧺", pantry: "🥫", closet: "👔",
};

const SPACE_TYPE_HINTS: Record<string, string> = {
  home: "Your entire dwelling",
  floor: "A level of your home",
  room: "A distinct area like kitchen or bedroom",
  container: "Something inside a room — closet, drawer, shelf",
};

/* Always-visible overflow menu for touch devices */
function SpaceOverflowMenu({ space, onEdit, onAddItem, onAddSubSpace, onDelete }: {
  space: Space; onEdit: () => void; onAddItem: () => void; onAddSubSpace: () => void; onDelete: () => void;
}) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className="relative md:hidden" ref={ref}>
      <button onClick={(e) => { e.stopPropagation(); setOpen(!open); }} aria-expanded={open} aria-haspopup="true" aria-label={`Actions for ${space.name}`} className="p-1.5 rounded-md hover:bg-amethyst-soft">
        <MoreHorizontal className="w-4 h-4 text-faint" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-md border border-clay-border shadow-lg z-50 py-1" role="menu">
          <button role="menuitem" onClick={() => { onEdit(); setOpen(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-body hover:bg-amethyst-soft">
            <Pencil className="w-3.5 h-3.5 text-primary" />Edit
          </button>
          <button role="menuitem" onClick={() => { onAddItem(); setOpen(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-body hover:bg-amethyst-soft">
            <Plus className="w-3.5 h-3.5 text-primary" />Add item here
          </button>
          <button role="menuitem" onClick={() => { onAddSubSpace(); setOpen(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-body hover:bg-amethyst-soft">
            <Package className="w-3.5 h-3.5 text-primary" />Add sub-space
          </button>
          <button role="menuitem" onClick={() => { onDelete(); setOpen(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-error-text hover:bg-error-wash">
            <Trash2 className="w-3.5 h-3.5" />Delete
          </button>
        </div>
      )}
    </div>
  );
}

export default function SpacesPage() {
  const [, navigate] = useLocation();
  const toast = useToast();
  const { user } = useAuth();
  const [spaces, setSpaces] = React.useState<Space[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(false);
  const [showAdd, setShowAdd] = React.useState(false);
  const [addMode, setAddMode] = React.useState<"single" | "bulk">("single");
  const [deleteTarget, setDeleteTarget] = React.useState<Space | null>(null);
  const [stats, setStats] = React.useState<Record<string, number>>({});
  const intentDismissKey = `ht_intent_nudge_dismissed_${user?.id}`;
  const [intentNudgeDismissed, setIntentNudgeDismissed] = React.useState(() => localStorage.getItem(`ht_intent_nudge_dismissed_${user?.id}`) === "1");
  // Re-sync the dismissal flag once the authenticated user id is available (the
  // state initializer runs before `user` may have loaded).
  React.useEffect(() => {
    setIntentNudgeDismissed(localStorage.getItem(intentDismissKey) === "1");
  }, [intentDismissKey]);
  const [showRoomSetup, setShowRoomSetup] = React.useState(false);

  // Single add
  const [newName, setNewName] = React.useState("");
  const [newType, setNewType] = React.useState("room");
  const [newParentId, setNewParentId] = React.useState<string | null>(null);

  const fetchSpaces = React.useCallback(async () => {
    setLoadError(false);
    try { setSpaces(await apiFetch<Space[]>("/api/spaces")); }
    catch { setLoadError(true); }
    setLoading(false);
  }, []);

  React.useEffect(() => { fetchSpaces(); }, [fetchSpaces]);

  React.useEffect(() => {
    apiFetch<{ spaceId: string; itemCount: number }[]>("/api/spaces/stats").then((s) => {
      const m: Record<string, number> = {};
      for (const r of s) if (r.spaceId) m[r.spaceId] = r.itemCount;
      setStats(m);
    }).catch(() => {});
  }, [spaces]);

  const handleAddSingle = async () => {
    if (!newName.trim()) return;
    try {
      await apiFetch("/api/spaces", { method: "POST", body: JSON.stringify({ name: newName, type: newType, parentId: newParentId }) });
      toast.show("Space created!");
      setNewName(""); setShowAdd(false); fetchSpaces();
    } catch (e: any) { toast.show(e.message ?? "Failed to create space", "error"); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiFetch(`/api/spaces/${deleteTarget.id}`, { method: "DELETE" });
      toast.show(`"${deleteTarget.name}" deleted`);
      setDeleteTarget(null); fetchSpaces();
    } catch (e: any) { toast.show(e.message ?? "Failed to delete space", "error"); }
  };

  const getIcon = (space: Space) => (space.roomType && ROOM_ICONS[space.roomType]) || ROOM_ICONS[space.name.toLowerCase()] || ROOM_ICONS[space.name.toLowerCase().replace(/\s*\d+$/, "")] || "📍";

  const childrenMap = React.useMemo(() => {
    const map = new Map<string | null, Space[]>();
    for (const s of spaces) {
      const list = map.get(s.parentId);
      if (list) list.push(s); else map.set(s.parentId, [s]);
    }
    return map;
  }, [spaces]);

  const renderTree = (parentId: string | null, depth = 0): React.ReactNode => {
    const children = childrenMap.get(parentId);
    if (!children?.length) return null;
    return children.map((space) => (
      <div key={space.id} style={{ marginLeft: depth * 20 }}>
          <div className="flex items-center gap-2 p-3 rounded-md hover:bg-amethyst-soft group">
            <span className="text-base">{getIcon(space)}</span>
            <span className="flex-1 text-sm font-medium text-ink cursor-pointer hover:text-primary" onClick={() => navigate(`/spaces/${space.id}`)}>{space.name}</span>
            <span className="text-[0.8125rem] text-faint uppercase tracking-wide">{space.type}</span>
            {stats[space.id] ? <button onClick={() => navigate(`/inventory?spaceId=${space.id}`)} className="px-2 py-0.5 rounded-pill bg-amethyst-soft text-amethyst-ink text-[0.8125rem] font-medium tabular-nums hover:bg-amethyst-soft/70 transition-colors cursor-pointer">{stats[space.id]} <Package className="w-2.5 h-2.5 inline" /></button> : null}
            {/* Desktop hover-reveal actions */}
            <button onClick={() => navigate(`/spaces/${space.id}`)} className="hidden md:block opacity-0 group-hover:opacity-100 p-1 hover:bg-amethyst-soft rounded-md" title="Edit space"><Pencil className="w-3.5 h-3.5 text-primary" /></button>
            <button onClick={() => navigate(`/inventory?action=add&spaceId=${space.id}`)} className="hidden md:block opacity-0 group-hover:opacity-100 p-1 hover:bg-amethyst-soft rounded-md" title="Add item here"><Plus className="w-3.5 h-3.5 text-primary" /></button>
            <button onClick={() => { setNewParentId(space.id); setAddMode("single"); setShowAdd(true); }} className="hidden md:block opacity-0 group-hover:opacity-100 p-1 hover:bg-amethyst-soft rounded-md" title="Add sub-space"><Package className="w-3.5 h-3.5 text-primary" /></button>
            <button onClick={() => setDeleteTarget(space)} aria-label={`Delete ${space.name}`} title="Delete space" className="hidden md:block opacity-0 group-hover:opacity-100 p-1 hover:bg-error-wash rounded-md"><Trash2 className="w-3.5 h-3.5 text-error" /></button>
            {/* Touch/mobile overflow menu — always visible */}
            <SpaceOverflowMenu
              space={space}
              onEdit={() => navigate(`/spaces/${space.id}`)}
              onAddItem={() => navigate(`/inventory?action=add&spaceId=${space.id}`)}
              onAddSubSpace={() => { setNewParentId(space.id); setAddMode("single"); setShowAdd(true); }}
              onDelete={() => setDeleteTarget(space)}
            />
          </div>
        {renderTree(space.id, depth + 1)}
      </div>
    ));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-ink flex items-center gap-2 font-headline tracking-[-0.01em]"><MapPin className="w-6 h-6 text-primary" />Spaces</h1>
        <div className="flex gap-2">
          <button onClick={() => { setNewParentId(null); setAddMode("single"); setShowAdd(true); }} className="px-4 py-2.5 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-1 hover:bg-amethyst-ink transition-colors"><Plus className="w-4 h-4" />Add Space</button>
        </div>
      </div>

      {spaces.every((s) => s.type === "home") && !loading && !loadError && (
        <div className="bg-sage-wash rounded-lg border border-clay-border p-8 text-center">
          <span className="text-4xl">🏠</span>
          <h2 className="text-[1.0625rem] font-semibold text-ink mt-3 mb-1">Your home is ready!</h2>
          <p className="text-sm text-body mb-6">Add rooms like Kitchen, Bedroom, and Bathroom to start organizing</p>
          <button onClick={() => { setAddMode("bulk"); setShowAdd(true); }} className="px-6 py-3 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors">Add Your Rooms</button>
        </div>
      )}

      {/* Single Add */}
      {showAdd && addMode === "single" && (
        <div className="bg-white rounded-lg border border-clay-border p-4 space-y-3">
          <input placeholder="Space name" value={newName} onChange={(e) => setNewName(e.target.value)} className="w-full px-3 py-2.5 rounded-sm border border-clay-border text-sm text-body placeholder:text-faint focus:border-primary focus:ring-2 focus:ring-primary/30 focus:outline-none" />
          <div className="flex gap-2">
            {["home", "floor", "room", "container"].map((t) => (
              <button key={t} onClick={() => setNewType(t)} className={cn("px-3 py-1 rounded-pill text-sm font-medium", newType === t ? "bg-primary text-white" : "bg-clay-muted text-body")}>{t}</button>
            ))}
          </div>
          {newType && <p className="text-[0.8125rem] text-faint -mt-1">{SPACE_TYPE_HINTS[newType]}</p>}
          {spaces.length > 0 && <SearchableSelect spaces={spaces} value={newParentId} onChange={setNewParentId} placeholder="Parent (optional)" />}
          <div className="flex gap-2">
            <button onClick={() => setShowAdd(false)} className="px-4 py-2.5 rounded-md bg-amethyst-soft text-amethyst-ink text-sm font-medium">Cancel</button>
            <button onClick={handleAddSingle} className="px-4 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors">Create</button>
          </div>
        </div>
      )}

      {/* Bulk Add Modal */}
      <Modal open={showAdd && addMode === "bulk"} onClose={() => setShowAdd(false)} title="Add Many Spaces" wide>
        <BulkSpacesWizard spaces={spaces} onComplete={async () => { setShowAdd(false); toast.show("Spaces created!"); await fetchSpaces(); setShowRoomSetup(true); }} />
      </Modal>

      {/* Space tree */}
      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-12 bg-clay-muted rounded-md animate-pulse" />)}</div>
      ) : loadError ? (
        <div className="bg-error-wash rounded-lg border border-clay-border text-center py-16 px-6">
          <p className="text-error-text mb-4">We couldn't load your spaces.</p>
          <button onClick={fetchSpaces} className="px-4 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors">Try again</button>
        </div>
      ) : spaces.length === 0 ? (
        <div className="bg-sage-wash rounded-lg border border-clay-border text-center py-16 px-6"><HomeIcon className="w-12 h-12 text-sage-deep mx-auto mb-3" /><p className="text-body">No spaces yet — add your first home to start organizing.</p></div>
      ) : !spaces.every((s) => s.type === "home") ? (
        <div className="bg-white rounded-lg border border-clay-border p-4">{renderTree(null)}</div>
      ) : null}

      {/* Space Intent discovery nudge */}
      {(() => {
        const eligibleSpaces = spaces.filter((s) => s.type === "room" || s.type === "container");
        // Only nudge for spaces that genuinely lack intent — neither user-defined
        // intent nor a room-type default (a bedroom's "sleep" default is enough).
        const spacesWithoutIntent = eligibleSpaces.filter((s) => !spaceHasIntent(s));
        if (intentNudgeDismissed || eligibleSpaces.length === 0 || spacesWithoutIntent.length === 0 || loading) return null;
        const firstTarget = spacesWithoutIntent[0];
        return (
          <div className="bg-amethyst-soft rounded-lg border border-clay-border p-5 flex items-start gap-4">
            <div className="w-10 h-10 rounded-md bg-white flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-lg">✨</span>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-[1.0625rem] font-semibold text-amethyst-ink mb-1">Define what your spaces are for</h3>
              <p className="text-sm text-body leading-relaxed mb-3">
                Tell Homethyst how you use each room — what activities happen there and how you want it to feel.
                This helps suggest routines and organization ideas tailored to your life.
              </p>
              <div className="flex items-center gap-3">
                <button onClick={() => navigate(`/spaces/${firstTarget.id}`)} className="px-3.5 py-2 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors">
                  Set up {firstTarget.name}
                </button>
                <span className="text-[0.8125rem] text-faint tabular-nums">{spacesWithoutIntent.length} space{spacesWithoutIntent.length > 1 ? "s" : ""} to set up</span>
              </div>
            </div>
            <button onClick={() => { localStorage.setItem(intentDismissKey, "1"); setIntentNudgeDismissed(true); }} aria-label="Dismiss" className="p-1 rounded-md hover:bg-white/60 text-faint hover:text-ink shrink-0">
              <span className="text-sm">✕</span>
            </button>
          </div>
        );
      })()}

      {/* Room Setup Flow — triggered after bulk space creation */}
      <Modal open={showRoomSetup} onClose={() => setShowRoomSetup(false)} title="Set Up a Room" wide>
        <RoomSetupFlow
          spaces={spaces}
          onComplete={() => { setShowRoomSetup(false); fetchSpaces(); }}
          onSkip={() => setShowRoomSetup(false)}
        />
      </Modal>

      <ConfirmDialog open={!!deleteTarget} title="Delete Space" message={`Delete "${deleteTarget?.name}" and everything inside it? Items in this space will become unlocated.`} confirmLabel="Delete" destructive onConfirm={handleDelete} onCancel={() => setDeleteTarget(null)} />
      <AdSlot slot="spaces-bottom" />
    </div>
  );
}
